if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');

        if (fsLightboxInstances['pdp-gallery']) {
          this.updatePlayButtonLightbox();

          fsLightboxInstances['pdp-gallery'].props.showThumbsOnMount = true;
          fsLightboxInstances['pdp-gallery'].props.onSlideChange = function (fsLightbox) {
            const sources = fsLightbox.elements.sources;

            for (let i = 0; i < sources.length; i++) {
              const source = sources[i];
              if (source && source.tagName === 'VIDEO' && !source.paused) {
                source.pause();
              }

              if (source && source.tagName === 'IFRAME') {
                const iframe = source.contentWindow;

                iframe.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
                iframe.postMessage('{"method":"pause","value":""}', '*');
              }
            }
          };
        }
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, this.handleOptionValueChange.bind(this));

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct ? this.handleSwapProduct(productUrl, shouldFetchFullPage) : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        // ADA: do not disable the button while the new variant loads (screen readers announced
        // "unavailable"); mark the form busy and block submits until the response arrives.
        this.setLoadingState(true);
        productForm?.handleErrorMessage();
      }

      setLoadingState(isLoading) {
        const productForm = this.productForm;
        if (!productForm) return;
        if (!this.boundBlockSubmitWhileLoading) {
          this.boundBlockSubmitWhileLoading = (event) => {
            if (this.dataset.variantLoading !== 'true') return;
            if (!this.productForm || !this.productForm.contains(event.target)) return;
            event.preventDefault();
            event.stopImmediatePropagation();
          };
          this.addEventListener('submit', this.boundBlockSubmitWhileLoading, true);
        }
        const submitButton = productForm.querySelector('[type="submit"]');
        if (isLoading) {
          this.dataset.variantLoading = 'true';
          productForm.setAttribute('aria-busy', 'true');
          submitButton?.setAttribute('aria-busy', 'true');
        } else {
          delete this.dataset.variantLoading;
          productForm.removeAttribute('aria-busy');
          submitButton?.removeAttribute('aria-busy');
        }
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.setLoadingState(false);
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              this.setLoadingState(false);
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          this.productShippingDetails?.reload(variant);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`).forEach(
          (productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        );
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(`${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`);

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        const sectionKey = this.sectionId;

        const syncStrip = (sourceUl, destinationUl) => {
          if (!sourceUl || !destinationUl) return;

          const refreshSourceData = () => {
            const sourceItems = Array.from(sourceUl.querySelectorAll('li[data-media-id]'));
            const sourceSet = new Set(sourceItems.map((item) => item.dataset.mediaId));
            const sourceMap = new Map(sourceItems.map((item, index) => [item.dataset.mediaId, { item, index }]));
            return [sourceItems, sourceSet, sourceMap];
          };

          let [sourceItems, sourceSet, sourceMap] = refreshSourceData();
          const destItems = Array.from(destinationUl.querySelectorAll('li[data-media-id]'));
          const destinationSet = new Set(destItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          for (let i = destItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(destItems[i].dataset.mediaId)) {
              sourceUl.prepend(destItems[i]);
              shouldRefresh = true;
            }
          }

          for (let i = 0; i < sourceItems.length; i++) {
            if (!destinationSet.has(sourceItems[i].dataset.mediaId)) {
              sourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          if (shouldRefresh) [sourceItems, sourceSet, sourceMap] = refreshSourceData();

          destItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              sourceUl.insertBefore(sourceData.item, sourceUl.querySelector(`li:nth-of-type(${destinationIndex + 1})`));

              [sourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        };

        const galleryDest = html.querySelector(`#Slider-Gallery-${sectionKey}`);
        syncStrip(this.querySelector(`#Slider-Gallery-${sectionKey}`), galleryDest);
        syncStrip(this.querySelector(`#Slider-Thumbnails-${sectionKey}`), html.querySelector(`#Slider-Thumbnails-${sectionKey}`));

        const mediaGallery = this.querySelector(`media-gallery`);
        let activeMediaId = null;

        if (variantFeaturedMediaId) {
          activeMediaId = `${this.dataset.section}-${variantFeaturedMediaId}`;
        } else if (galleryDest) {
          const activeSlide = galleryDest.querySelector('.product__media-item.is-active');
          if (activeSlide?.dataset?.mediaId) {
            activeMediaId = activeSlide.dataset.mediaId;
          } else {
            const firstVisible = galleryDest.querySelector('.product__media-item:not(.hide-image)');
            activeMediaId = firstVisible?.dataset?.mediaId ?? null;
          }
        }

        if (activeMediaId) {
          mediaGallery?.setActiveMedia?.(activeMediaId, true);
        }

        this.updateMediaGrouping(mediaGallery);
      }

      updateMediaGrouping(mediaGallery) {
        mediaGallery = mediaGallery || this.querySelector(`media-gallery`);

        if (!mediaGallery) return;

        const hasOnlyDefaultVariant = mediaGallery.getAttribute('data-has-only-default-variant');
        if (hasOnlyDefaultVariant === 'true') return;

        const mediaGalleryId = mediaGallery.id;
        const featuredMedia = mediaGallery.querySelector('.product__media-item.is-active');
        const featuredVariantGrouping = featuredMedia ? featuredMedia.getAttribute('data-variant-grouping') : null;

        document
          .querySelectorAll(`#${mediaGalleryId} .product__media-item, #${mediaGalleryId} .thumbnail-list__item`)
          .forEach((mediaItem) => {
            const mediaVariantGrouping = mediaItem.getAttribute('data-variant-grouping');

            if (mediaVariantGrouping && mediaVariantGrouping !== featuredVariantGrouping) {
              mediaItem.classList.add('hide-image');

              if (mediaItem.querySelector('.product__media')) {
                mediaItem.querySelector('.product__media').removeAttribute('data-fslightbox');
              }
            } else {
              mediaItem.classList.remove('hide-image');

              if (mediaItem.querySelector('.product__media')) {
                mediaItem.querySelector('.product__media').setAttribute('data-fslightbox', 'pdp-gallery');
              }
            }
          });

        this.updatePlayButtonLightbox();

        refreshFsLightbox();
      }

      updatePlayButtonLightbox() {
        const mediaGallery = this.querySelector(`media-gallery`);
        if (!mediaGallery) return;

        const featuredMedia = mediaGallery.querySelector('.product__media-item.is-active');
        const featuredVariantGrouping = featuredMedia ? featuredMedia.getAttribute('data-variant-grouping') : null;
        const svgList = [];
        const parser = new DOMParser();
        const playIcon =
          '<svg width="66" height="66" viewBox="0 0 66 66" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M32.9999 1.6499C15.7086 1.6499 1.6499 15.7086 1.6499 32.9999C1.6499 50.2912 15.7086 64.3499 32.9999 64.3499C50.2912 64.3499 64.3499 50.2912 64.3499 32.9999C64.3499 15.7086 50.2912 1.6499 32.9999 1.6499ZM43.0986 34.5826L28.3132 44.4179C27.0603 45.2764 25.3432 44.3509 25.3432 42.835V23.1663C25.3432 21.6478 27.0603 20.7248 28.3132 21.5834L43.0959 31.416C44.22 32.1405 44.22 33.8581 43.0986 34.5826Z" fill="currentColor"/></svg>';
        const svg = parser.parseFromString(playIcon, 'text/html').querySelector('svg');

        document.querySelectorAll(`#${mediaGallery.id} .product__media-item`).forEach((mediaItem) => {
          const mediaVariantGrouping = mediaItem.getAttribute('data-variant-grouping');
          const productMedia = mediaItem.querySelector('.product__media');
          if (!productMedia) return;

          if (!mediaVariantGrouping || mediaVariantGrouping === featuredVariantGrouping) {
            productMedia.classList.add('activate-fslightbox');

            if (productMedia.hasAttribute('data-gallery-video')) {
              svgList.push(svg);
            } else {
              svgList.push(undefined);
            }
          } else {
            productMedia.classList.remove('activate-fslightbox');
          }
        });

        fsLightboxInstances['pdp-gallery'].props.thumbsIcons = svgList;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get productShippingDetails() {
        return this.querySelector(`product-shipping-details`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(SectionId.parseId(this.sectionId), 'related-products');
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(SectionId.parseId(this.sectionId), 'quick_order_list');
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}